import React, { useState } from 'react';
import {
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { colors } from '../../theme/colors';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import api from '../../api/axiosConfig';
import { endpoints } from '../../api/endpoints';
import { logout } from '../../store/slices/authSlice';

const UserProfileModal: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/my-profile');
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    navigate('/change-password');
  };

  const handleLogout = async () => {
    handleMenuClose();

    const refreshToken = localStorage.getItem('refresh_token') || '';
    setIsLoggingOut(true);

    try {
      await api.post(endpoints.auth.logout, {
        refresh_token: refreshToken,
      });
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      dispatch(logout());
      setIsLoggingOut(false);
      navigate('/login', { replace: true });
    }
  };

  return (
    <>
      {/* Avatar Button */}
      <IconButton 
        onClick={handleAvatarClick}
        sx={{ 
          p: 0,
          '&:hover': {
            transform: 'scale(1.05)',
            transition: 'transform 0.2s',
          }
        }}
      >
        <Avatar 
          sx={{ 
            bgcolor: colors.primary, 
            width: 40, 
            height: 40,
            cursor: 'pointer',
            fontWeight: 'bold',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          A
        </Avatar>
      </IconButton>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            mt: 1,
            minWidth: 220,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: '1px solid #E2E8F0',
          }
        }}
      >
        <MenuItem onClick={handleProfileClick} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" sx={{ color: colors.black }} />
          </ListItemIcon>
          <ListItemText 
            primary="My Profile"
            // primaryTypographyProps={{ fontSize: '0.9rem' }}
          />
        </MenuItem>
        
        <MenuItem onClick={handleSettingsClick} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" sx={{ color: colors.black }} />
          </ListItemIcon>
          <ListItemText 
            primary="Account Settings"
            // primaryTypographyProps={{ fontSize: '0.9rem' }}
          />
        </MenuItem>
        
        <MenuItem onClick={handleLogout} sx={{ py: 1.5 }} disabled={isLoggingOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: colors.black }} />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            // primaryTypographyProps={{ 
            //   fontSize: '0.9rem',
            //   sx: { color: '#EF4444', fontWeight: 500 }
            // }}
          />
        </MenuItem>
      </Menu>

      {/* Profile Dialog */}
      {/* <Dialog 
        open={profileDialogOpen} 
        onClose={() => setProfileDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 3,
            bgcolor: '#F8FAFC',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #E2E8F0',
          pb: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B' }}>
            My Profile
          </Typography>
          <IconButton onClick={() => setProfileDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                bgcolor: '#3B82F6',
                fontSize: 48,
                fontWeight: 'bold',
                mb: 2,
                boxShadow: 3,
              }}
            >
              {userProfile.name.charAt(0)}
            </Avatar>
            {!isEditing && (
              <Button
                startIcon={<EditIcon />}
                onClick={handleEditToggle}
                size="small"
                sx={{ mt: 1 }}
              >
                Edit Profile
              </Button>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Full Name"
                name="name"
                value={isEditing ? editForm.name : userProfile.name}
                onChange={handleInputChange}
                disabled={!isEditing}
                variant="outlined"
                size="small"
                // InputProps={{
                //   startAdornment: (
                //     <AccountCircleIcon sx={{ color: '#64748B', mr: 1 }} />
                //   ),
                // }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Role"
                name="role"
                value={isEditing ? editForm.role : userProfile.role}
                onChange={handleInputChange}
                disabled={!isEditing}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={isEditing ? editForm.email : userProfile.email}
                onChange={handleInputChange}
                disabled={!isEditing}
                variant="outlined"
                size="small"
                // InputProps={{
                //   startAdornment: (
                //     <EmailIcon sx={{ color: '#64748B', mr: 1 }} />
                //   ),
                // }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={isEditing ? editForm.phone : userProfile.phone}
                onChange={handleInputChange}
                disabled={!isEditing}
                variant="outlined"
                size="small"
                // InputProps={{
                //   startAdornment: (
                //     <PhoneIcon sx={{ color: '#64748B', mr: 1 }} />
                //   ),
                // }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={isEditing ? editForm.location : userProfile.location}
                onChange={handleInputChange}
                disabled={!isEditing}
                variant="outlined"
                size="small"
                // InputProps={{
                //   startAdornment: (
                //     <LocationOnIcon sx={{ color: '#64748B', mr: 1 }} />
                //   ),
                // }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Department"
                name="department"
                value={isEditing ? editForm.department : userProfile.department}
                onChange={handleInputChange}
                disabled={!isEditing}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Join Date"
                name="joinDate"
                value={isEditing ? editForm.joinDate : userProfile.joinDate}
                onChange={handleInputChange}
                disabled={!isEditing}
                variant="outlined"
                size="small"
                // InputProps={{
                //   startAdornment: (
                //     <CalendarIcon sx={{ color: '#64748B', mr: 1 }} />
                //   ),
                // }}
              />
            </Grid>
          </Grid>

          {isEditing && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
              <Button onClick={handleCancelEdit} variant="outlined" color="secondary">
                Cancel
              </Button>
              <Button onClick={handleSaveProfile} variant="contained" sx={{ bgcolor: '#3B82F6' }}>
                Save Changes
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog> */}

      {/* Account Settings Dialog */}
      <Dialog 
        open={settingsDialogOpen} 
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #E2E8F0',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Account Settings</Typography>
          <IconButton onClick={() => setSettingsDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Current Password"
              type="password"
              size="small"
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              size="small"
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              size="small"
            />
            
            <Divider sx={{ my: 1 }} />
            
            <TextField
              fullWidth
              label="Email Notifications"
              select
              size="small"
              defaultValue="enabled"
            >
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </TextField>
            
            <TextField
              fullWidth
              label="Language"
              select
              size="small"
              defaultValue="english"
            >
              <MenuItem value="english">English</MenuItem>
              <MenuItem value="hindi">Hindi</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setSettingsDialogOpen(false)} variant="contained" sx={{ bgcolor: '#3B82F6' }}>
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserProfileModal;